import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Sparkles, Heart, ShoppingBag, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: Home, match: (p) => p === '/' },
  { to: '/products', label: 'Shop', icon: Sparkles, match: (p) => p.startsWith('/products') || p.startsWith('/product/') },
  { to: '/wishlist', label: 'Wishlist', icon: Heart, match: (p) => p.startsWith('/wishlist'), requiresAuth: true },
  { to: '/cart', label: 'Cart', icon: ShoppingBag, match: (p) => p.startsWith('/cart') || p.startsWith('/checkout'), requiresAuth: true },
  { to: '/orders', label: 'Account', icon: User, match: (p) => p.startsWith('/orders') || p.startsWith('/login'), requiresAuth: true },
];

const BottomNav = ({ cartCount = 0, wishlistCount = 0 }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Hide on admin and login screens
  if (location.pathname.startsWith('/admin') || location.pathname === '/login') return null;

  return (
    <nav
      data-testid="bottom-mobile-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#2D3B26]/10 shadow-[0_-4px_20px_rgba(45,59,38,0.06)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="grid grid-cols-5">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = item.match(location.pathname);
          const target = item.requiresAuth && !user ? '/login' : item.to;
          const badge =
            item.label === 'Cart' ? cartCount :
            item.label === 'Wishlist' ? wishlistCount : 0;

          return (
            <li key={item.label}>
              <Link
                to={target}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 transition-colors duration-200 ${
                  isActive ? 'text-[#C27054]' : 'text-[#2D3B26]/70 hover:text-[#C27054]'
                }`}
              >
                <span className="relative">
                  <Icon
                    size={20}
                    strokeWidth={isActive ? 2.4 : 1.8}
                    className={isActive ? 'fill-[#C27054]/15' : ''}
                  />
                  {badge > 0 && (
                    <span
                      data-testid={`bottom-nav-${item.label.toLowerCase()}-badge`}
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-[#C27054] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none"
                    >
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </span>
                <span className={`text-[10px] tracking-wide ${isActive ? 'font-semibold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BottomNav;
